<template>
  <section class="demo-customers" :dir="isEnglish ? 'ltr' : 'rtl'" aria-labelledby="demo-customers-title">
    <div class="demo-customers__heading">
      <h2 id="demo-customers-title">{{ isEnglish ? 'Trusted by our customers across the region' : 'ثقة عملاؤنا في مختلف المناطق' }}</h2>
      <button v-if="filteredClients.length > 1" type="button" class="demo-customers__pause" :aria-pressed="paused" @click="paused = !paused">
        {{ paused ? (isEnglish ? 'Play' : 'تشغيل الحركة') : (isEnglish ? 'Pause' : 'إيقاف الحركة') }}
      </button>
    </div>

    <div class="demo-customers__regions" role="group" :aria-label="isEnglish ? 'Filter customers by region' : 'تصفية العملاء حسب المنطقة'">
      <button
        v-for="region in regions" :key="region.id" type="button"
        class="demo-customers__region" :class="{ 'is-selected': selectedRegion === region.id }"
        :style="{ '--region-color': region.color }" :aria-pressed="selectedRegion === region.id"
        aria-controls="demo-customers-list" @click="selectedRegion = region.id"
      >
        <span class="demo-customers__region-fill" aria-hidden="true"></span>
        <span class="demo-customers__region-label"><span aria-hidden="true">⊕</span> {{ isEnglish ? region.en : region.ar }} <span aria-hidden="true">←</span></span>
      </button>
    </div>

    <div
      id="demo-customers-list" :key="selectedRegion" class="demo-customers__viewport"
      :class="{ 'is-paused': paused, 'is-single': filteredClients.length === 1 }"
      tabindex="0" role="region" :aria-label="isEnglish ? activeRegion.en + ' customers' : 'عملاء ' + activeRegion.ar"
      @touchstart.passive="paused = true"
    >
      <div class="demo-customers__track" :style="{ animationDuration: Math.max(30, filteredClients.length * 7) + 's' }">
        <div v-for="copy in (filteredClients.length > 1 ? 2 : 1)" :key="copy" class="demo-customers__group" :aria-hidden="copy === 2 ? 'true' : null">
          <article v-for="(client, index) in filteredClients" :key="index" class="demo-customers__card" :dir="isEnglish ? 'ltr' : 'rtl'">
            <div class="demo-customers__logo">
              <img v-if="client.logo && !failedLogos[client.logo]" :src="client.logo" :alt="clientName(client)" loading="lazy" decoding="async" @error="markLogoFailed(client.logo)">
              <span v-else class="demo-customers__logo-fallback" aria-hidden="true">{{ clientName(client).charAt(0) }}</span>
            </div>
            <h3 :title="clientName(client)">{{ clientName(client) }}</h3>
            <p :title="clientAddress(client)">{{ clientAddress(client) }}</p>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// Snapshot of https://carerp.io/ar/clients_data.json. Kept local because the
// source does not send Access-Control-Allow-Origin for demo installations.
import clients from '../data/clients_data.json';

export default {
  name: 'DemoCustomers',
  props: { language: { type: String, default: 'ar' } },
  data() {
    return {
      selectedRegion: 'central',
      paused: false,
      failedLogos: {},
      regions: [
        { id: 'central', ar: 'المنطقة الوسطى', en: 'Central region', color: '#7057ff' },
        { id: 'eastern', ar: 'المنطقة الشرقية', en: 'Eastern region', color: '#00ad83' },
        { id: 'western', ar: 'المنطقة الغربية', en: 'Western region', color: '#f28b24' },
        { id: 'northern', ar: 'المنطقة الشمالية', en: 'Northern region', color: '#ed4a89' },
        { id: 'southern', ar: 'المنطقة الجنوبية', en: 'Southern region', color: '#2689dc' },
        { id: 'uae', ar: 'الإمارات', en: 'UAE', color: '#9960ef' }
      ]
    };
  },
  computed: {
    isEnglish() { return this.language === 'en'; },
    filteredClients() { return clients.filter(client => client.region === this.selectedRegion); },
    activeRegion() { return this.regions.find(region => region.id === this.selectedRegion); }
  },
  methods: {
    clientName(client) { return (this.isEnglish && String(client.client_name_en || '').trim()) || client.client_name; },
    clientAddress(client) { return (this.isEnglish && String(client.address_en || '').trim()) || client.address; },
    markLogoFailed(logo) { this.$set(this.failedLogos, logo, true); }
  }
};
</script>

<style scoped>
.demo-customers {
  --customer-ease-out: cubic-bezier(0.23, 1, 0.32, 1);
  position: fixed;
  z-index: 4100;
  bottom: 50px;
  left: 16px;
  right: 16px;
  height: var(--demo-customers-height, 300px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  color: #202124;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, .1);
}
.demo-customers__heading { display: flex; align-items: center; justify-content: center; gap: 16px; }
.demo-customers__heading h2 { margin: 0; font-size: 19px; line-height: 1.4; font-weight: 700; }
.demo-customers__pause { flex-shrink: 0; font: inherit; font-size: 12px; color: #52525b; text-decoration: underline; }
.demo-customers__regions { display: flex; justify-content: center; gap: 10px; flex-shrink: 0; overflow-x: auto; padding: 4px; }
.demo-customers__region {
  position: relative;
  isolation: isolate;
  flex: 0 0 auto;
  min-height: 44px;
  padding: 10px 20px;
  overflow: hidden;
  border: 0;
  border-left: 5px solid var(--region-color);
  background: #dadcde;
  color: #202124;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 160ms var(--customer-ease-out);
}
.demo-customers__region-fill {
  position: absolute;
  inset: 0;
  z-index: -1;
  background: var(--region-color);
  transform: translateX(-102%);
  transition: transform 200ms var(--customer-ease-out);
}
.demo-customers__region.is-selected { color: #fff; }
.demo-customers__region.is-selected .demo-customers__region-fill { transform: translateX(0); }
.demo-customers__region-label { position: relative; display: flex; align-items: center; gap: 6px; }
.demo-customers__region:active { transform: scale(.97); }
.demo-customers button:focus-visible, .demo-customers__viewport:focus-visible { outline: 2px solid #4338ca; outline-offset: 2px; }
.demo-customers__viewport { flex: 1; min-height: 0; overflow-x: auto; overflow-y: hidden; direction: ltr; }
.demo-customers__track { display: flex; width: max-content; height: 100%; animation: demo-customer-scroll linear infinite; }
.demo-customers__group { display: flex; gap: 16px; padding-right: 16px; height: 100%; }
.demo-customers__card {
  flex: 0 0 204px;
  width: 204px;
  height: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #ececec;
  border-radius: 7px;
  background: #fff;
  text-align: center;
}
.demo-customers__logo { flex: 1; min-height: 0; width: 100%; display: flex; justify-content: center; align-items: center; margin-bottom: 8px; }
.demo-customers__logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
.demo-customers__logo-fallback { display: grid; place-items: center; width: 56px; height: 56px; border-radius: 50%; background: #f0edff; color: #7057ff; font-size: 28px; }
.demo-customers__card h3, .demo-customers__card p { width: 100%; overflow: hidden; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.demo-customers__card h3 { flex-shrink: 0; margin: 0; font-size: 13px; line-height: 1.4; font-weight: 700; }
.demo-customers__card p { flex-shrink: 0; margin: 4px 0 0; font-size: 11px; line-height: 1.4; color: #666; }
.demo-customers__viewport:focus-within .demo-customers__track,
.demo-customers__viewport.is-paused .demo-customers__track { animation-play-state: paused; }
.demo-customers__viewport.is-single .demo-customers__track { animation: none; width: 100%; justify-content: center; }
@keyframes demo-customer-scroll { to { transform: translateX(-50%); } }
@media (hover: hover) and (pointer: fine) {
  .demo-customers__region:hover { transform: translateY(-2px); }
  .demo-customers__region:not(.is-selected):hover .demo-customers__region-fill { transform: translateX(-88%); }
  .demo-customers__viewport:hover .demo-customers__track { animation-play-state: paused; }
}
@media (max-width: 1100px) { .demo-customers__regions { justify-content: flex-start; } }
@media (max-width: 600px) {
  .demo-customers { left: 8px; right: 8px; padding: 10px; gap: 8px; }
  .demo-customers__heading { justify-content: space-between; gap: 8px; }
  .demo-customers__heading h2 { font-size: 15px; }
  .demo-customers__region { padding: 8px 12px; font-size: 12px; }
  .demo-customers__card { flex-basis: 168px; width: 168px; }
}
@media (max-height: 600px) {
  .demo-customers { gap: 6px; padding: 8px 12px; }
  .demo-customers__heading h2 { font-size: 15px; }
  .demo-customers__region { min-height: 36px; padding: 6px 12px; }
  .demo-customers__card { padding: 6px 10px; }
  .demo-customers__card p { -webkit-line-clamp: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .demo-customers__pause { display: none; }
  .demo-customers__track { animation: none; }
  .demo-customers__group[aria-hidden="true"] { display: none; }
  .demo-customers__region, .demo-customers__region-fill { transition: opacity 160ms ease; transform: none !important; }
  .demo-customers__region-fill { opacity: 0; }
  .demo-customers__region.is-selected .demo-customers__region-fill { opacity: 1; }
}
</style>
